import React, { useState, useEffect, useRef } from 'react';
import { classNames } from 'primereact/utils';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

import AppTopbar from './AppTopbar';
import AppFooter from './AppFooter';
import AppMenu from './AppMenu';
import AppBreadcrumb from './AppBreadcrumb';

import Dashboard from './components/Dashboard';
import PurchaseOrderList from './components/PurchaseOrderList';
import PurchaseOrderDetails from './components/purchase-order/PurchaseOrderDetails';
import CreatePurchaseOrder from './components/CreatePurchaseOrder';
import WorkOrderList from './components/WorkOrderList';
import WorkOrderDetails from './components/WorkOrderDetails';
import ClientManagement from './components/ClientManagement';
import { ProductManagement } from './components/ProductManagement';
import UserManagement from './components/UserManagement';
import QuickbooksTokenManagement from './components/QuickbooksTokenManagement';
import Login from './components/Login';
import PurchaseOrderReport from './components/PurchaseOrderReport';
import TruckStatus from './components/TruckStatus';
import AgingReport from './components/AgingReport';
import ClientOrderAnalysis from './components/ClientOrderAnalysis';
import TopProductsReport from './components/TopProductsReport';
import TicketList from './components/TicketList';
import TicketForm from './components/TicketForm';
import TicketDetails from './components/TicketDetails';
import HandlingUnitBoard from './components/HandlingUnitBoard';

import PrimeReact from 'primereact/api';
import { Tooltip } from 'primereact/tooltip';
import { InputSwitchChangeEvent } from 'primereact/inputswitch';

import 'primereact/resources/primereact.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';
import './styles/layout/layout.scss';
import './styles/primereact-theme.css';

import { useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import { UserRole } from './types/user';

const App: React.FC = () => {
    const [layoutMode, setLayoutMode] = useState('static');
    const [layoutColorMode, setLayoutColorMode] = useState('light');
    const [inputStyle, setInputStyle] = useState('outlined');
    const [ripple, setRipple] = useState(true);
    const [staticMenuInactive, setStaticMenuInactive] = useState(false);
    const [overlayMenuActive, setOverlayMenuActive] = useState(false);
    const [mobileMenuActive, setMobileMenuActive] = useState(false);
    const copyTooltipRef = useRef<Tooltip>(null);
    const location = useLocation();
    const navigate = useNavigate();
    const { token, role, logout } = useAuth();

    PrimeReact.ripple = true;

    let menuClick = false;

    useEffect(() => {
        if (mobileMenuActive) {
            addClass(document.body, 'body-overflow-hidden');
        } else {
            removeClass(document.body, 'body-overflow-hidden');
        }
    }, [mobileMenuActive]);

    useEffect(() => {
        if (!token && location.pathname !== '/login') {
            navigate('/login');
        }
    }, [token, location.pathname, navigate]);

    const onInputStyleChange = (inputStyle: string) => {
        setInputStyle(inputStyle);
    };

    const onRipple = (e: InputSwitchChangeEvent) => {
        PrimeReact.ripple = e.value as boolean;
        setRipple(e.value as boolean);
    };

    const onLayoutModeChange = (mode: string) => {
        setLayoutMode(mode);
    };

    const onColorModeChange = (mode: string) => {
        setLayoutColorMode(mode);
    };

    const onWrapperClick = (event: React.MouseEvent<HTMLDivElement>) => {
        if (!menuClick) {
            setOverlayMenuActive(false);
            setMobileMenuActive(false);
        }
        menuClick = false;
    };

    const onToggleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        menuClick = true;
        setMobileMenuActive((prevState) => !prevState);
        event.preventDefault();
    };

    const onSidebarClick = () => {
        menuClick = true;
    };

    const handleLogout = async () => {
        try {
            await logout();
            localStorage.removeItem('token');
            delete axios.defaults.headers.common['Authorization'];
            navigate('/login');
        } catch (error) {
            console.error('Logout error:', error);
            navigate('/login');
        }
    };

    const onMenuItemClick = (event: { originalEvent: React.MouseEvent<HTMLAnchorElement, MouseEvent>; item: any }) => {
        if (event.item.command) {
            event.item.command();
            return;
        }
        
        if (!event.item.items) {
            setOverlayMenuActive(false);
            setMobileMenuActive(false);
        }
    };

    const isDesktop = () => {
        return window.innerWidth >= 992;
    };

    const menu = [
        {
            label: 'Home',
            icon: 'pi pi-fw pi-home',
            items: [
                { label: 'Dashboard', icon: 'pi pi-fw pi-home', to: '/' },
                { 
                    label: 'Handling Unit Board', 
                    icon: 'pi pi-fw pi-table', 
                    to: '/handling-unit-board',
                    visible: role !== UserRole.Customer
                }
            ]
        },
        {
            label: 'Purchase Orders',
            icon: 'pi pi-fw pi-file',
            items: [
                { label: 'List', icon: 'pi pi-fw pi-list', to: '/purchase-orders' },
                { label: 'Create', icon: 'pi pi-fw pi-plus', to: '/create-purchase-order' }
            ]
        },
        {
            label: 'Tickets',
            icon: 'pi pi-fw pi-ticket',
            visible: role !== UserRole.Customer,
            items: [
                { label: 'List', icon: 'pi pi-fw pi-list', to: '/tickets' },
                { label: 'Create', icon: 'pi pi-fw pi-plus', to: '/tickets/create' }
            ]
        },
        {
            label: 'Shipments',
            icon: 'pi pi-fw pi-truck',
            items: [
                { 
                    label: 'Truck Status', 
                    icon: 'pi pi-fw pi-list', 
                    to: '/truck-status'
                }
            ]
        },
        {
            label: 'Reports',
            icon: 'pi pi-fw pi-chart-bar',
            visible: role !== UserRole.Customer,
            items: [
                { 
                    label: 'Revenue Report', 
                    icon: 'pi pi-fw pi-chart-line', 
                    to: '/purchase-order-report'
                },
                {
                    label: 'Aging Report',
                    icon: 'pi pi-fw pi-clock',
                    to: '/aging-report'
                },
                {
                    label: 'Client Order Analysis',
                    icon: 'pi pi-fw pi-users',
                    to: '/client-order-analysis'
                },
                {
                    label: 'Top Products Report',
                    icon: 'pi pi-fw pi-box',
                    to: '/top-products-report'
                }
            ]
        },
        {
            label: 'Work Orders',
            icon: 'pi pi-fw pi-briefcase',
            visible: false,
            items: [{ label: 'List', icon: 'pi pi-fw pi-list', to: '/work-orders' }]
        },
        {
            label: 'Management',
            icon: 'pi pi-fw pi-cog',
            visible: role === UserRole.Admin,
            items: [
                { label: 'Clients', icon: 'pi pi-fw pi-users', to: '/clients' },
                { label: 'Products', icon: 'pi pi-fw pi-box', to: '/products' },
                { label: 'Users', icon: 'pi pi-fw pi-user', to: '/users' },
                { label: 'QuickBooks', icon: 'pi pi-fw pi-money-bill', to: '/quickbooks' }
            ]
        },
        {
            label: 'Account',
            icon: 'pi pi-fw pi-user',
            items: [
                { 
                    label: 'Logout', 
                    icon: 'pi pi-fw pi-power-off',
                    command: handleLogout
                }
            ]
        }
    ];

    const addClass = (element: HTMLElement, className: string) => {
        if (element.classList)
            element.classList.add(className);
        else
            element.className += ' ' + className;
    };

    const removeClass = (element: HTMLElement, className: string) => {
        if (element.classList)
            element.classList.remove(className);
        else
            element.className = element.className.replace(new RegExp('(^|\\b)' + className.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
    };

    const wrapperClass = classNames('layout-wrapper', {
        'layout-overlay': layoutMode === 'overlay',
        'layout-static': layoutMode === 'static',
        'layout-static-sidebar-inactive': staticMenuInactive && layoutMode === 'static',
        'layout-overlay-sidebar-active': overlayMenuActive && layoutMode === 'overlay',
        'layout-mobile-sidebar-active': mobileMenuActive,
        'p-input-filled': inputStyle === 'filled',
        'p-ripple-disabled': ripple === false,
        'layout-theme-light': layoutColorMode === 'light'
    });

    if (location.pathname === '/login') {
        return <Login />;
    }

    if (!token) {
        return null;
    }

    return (
        <ErrorBoundary>
            <div className={wrapperClass} onClick={onWrapperClick}>
                <Tooltip ref={copyTooltipRef} target=".block-action-copy" position="bottom" content="Copied to clipboard" event="focus" />

                <AppTopbar 
                    onToggleMenuClick={onToggleMenuClick} 
                    layoutColorMode={layoutColorMode}
                    mobileTopbarMenuActive={mobileMenuActive}
                    onMobileTopbarMenuClick={onToggleMenuClick}
                    onMobileSubTopbarMenuClick={onToggleMenuClick} 
                />

                <div className="layout-sidebar" onClick={onSidebarClick}>
                    <AppMenu model={menu} onMenuItemClick={onMenuItemClick} layoutColorMode={layoutColorMode} />
                </div>

                <div className="layout-main-container">
                    <div className="layout-main">
                        <AppBreadcrumb />
                        <Routes>
                            <Route path="/login" element={<Login />} />
                            <Route path="/" element={<ProtectedRoute element={<Dashboard />} />} />
                            <Route path="/handling-unit-board" element={
                                <ProtectedRoute 
                                    element={<HandlingUnitBoard />}
                                    allowedRoles={[UserRole.Admin, UserRole.Manager, UserRole.User, UserRole.Warehouse]}
                                />
                            } />
                            
                            {/* Ticket Routes */}
                            <Route path="/tickets" element={
                                <ProtectedRoute 
                                    element={<TicketList />} 
                                    allowedRoles={[UserRole.Admin, UserRole.Manager, UserRole.User, UserRole.Warehouse]}
                                />
                            } />
                            <Route path="/tickets/create" element={
                                <ProtectedRoute 
                                    element={<TicketForm />} 
                                    allowedRoles={[UserRole.Admin, UserRole.Manager, UserRole.User, UserRole.Warehouse]}
                                />
                            } />
                            <Route path="/tickets/edit/:id" element={
                                <ProtectedRoute 
                                    element={<TicketForm />} 
                                    allowedRoles={[UserRole.Admin, UserRole.Manager, UserRole.User, UserRole.Warehouse]}
                                />
                            } />
                            <Route path="/tickets/:id" element={
                                <ProtectedRoute 
                                    element={<TicketDetails />} 
                                    allowedRoles={[UserRole.Admin, UserRole.Manager, UserRole.User, UserRole.Warehouse]}
                                />
                            } />
                            
                            <Route path="/purchase-orders" element={<ProtectedRoute element={<PurchaseOrderList />} />} />
                            <Route path="/purchase-orders/:id" element={<ProtectedRoute element={<PurchaseOrderDetails />} />} />
                            <Route
                                path="/create-purchase-order"
                                element={
                                    <ProtectedRoute
                                        element={<CreatePurchaseOrder />}
                                        allowedRoles={[UserRole.Admin, UserRole.Manager, UserRole.Customer, UserRole.Warehouse]}
                                    />
                                }
                            />
                            <Route
                                path="/purchase-order-report"
                                element={
                                    <ProtectedRoute
                                        element={<PurchaseOrderReport />}
                                        allowedRoles={[UserRole.Admin, UserRole.Manager]}
                                    />
                                }
                            />
                            <Route
                                path="/aging-report"
                                element={
                                    <ProtectedRoute
                                element={<AgingReport />}
                                allowedRoles={[UserRole.Admin, UserRole.Manager, UserRole.Warehouse]}
                                    />
                                }
                            />
                            <Route
                                path="/client-order-analysis"
                                element={
                                    <ProtectedRoute
                                        element={<ClientOrderAnalysis />}
                                        allowedRoles={[UserRole.Admin, UserRole.Manager]}
                                    />
                                }
                            />
                            <Route
                                path="/top-products-report"
                                element={
                                    <ProtectedRoute
                                        element={<TopProductsReport />}
                                        allowedRoles={[UserRole.Admin, UserRole.Manager]}
                                    />
                                }
                            />
                            <Route
                                path="/truck-status"
                                element={<ProtectedRoute element={<TruckStatus />} />}
                            />
                            <Route path="/work-orders" element={<ProtectedRoute element={<WorkOrderList />} />} />
                            <Route path="/work-orders/:id" element={<ProtectedRoute element={<WorkOrderDetails />} />} />
                            <Route
                                path="/clients"
                                element={
                                    <ProtectedRoute
                                        element={<ClientManagement />}
                                        allowedRoles={[UserRole.Admin, UserRole.Manager]}
                                    />
                                }
                            />
                            <Route
                                path="/products"
                                element={
                                    <ProtectedRoute
                                        element={<ProductManagement />}
                                        allowedRoles={[UserRole.Admin, UserRole.Manager]}
                                    />
                                }
                            />
                            <Route
                                path="/users"
                                element={
                                    <ProtectedRoute
                                        element={<UserManagement />}
                                        allowedRoles={[UserRole.Admin]}
                                    />
                                }
                            />
                            <Route
                                path="/quickbooks"
                                element={
                                    <ProtectedRoute
                                        element={<QuickbooksTokenManagement />}
                                        allowedRoles={[UserRole.Admin]}
                                    />
                                }
                            />
                        </Routes>
                    </div>

                    <AppFooter layoutColorMode={layoutColorMode} />
                </div>

                <div className="layout-mask"></div>
            </div>
        </ErrorBoundary>
    );
};

export default App;
